import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { Section, EmptyState } from "../../components/vc/layout";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { BusinessMultiSelect } from "../../components/vc/BusinessMultiSelect";
import { QuoteImagePane } from "../../components/vc/QuoteImagePane";
import {
  ArrowLeft, Plus, Trash2, ShoppingBag, Building2, Sparkles, X, CornerDownLeft,
} from "lucide-react";
import { toast } from "sonner";

const UNITS = ["pcs", "kg", "ltr", "box", "bag", "meter", "gram", "dozen"];

/**
 * Order Review — Excel/Manual/Image → per-item business assignment.
 * #14: each item can be routed to different businesses via a searchable dropdown.
 * The backend groups items per supplier and creates one order per supplier.
 */
export default function VCOrderReview() {
  const location = useLocation();
  const navigate = useNavigate();
  const { items: initItems = [], image_data, images: initImages = [] } = location.state || {};

  const [rows, setRows] = useState(() =>
    (initItems.length ? initItems : [{ product: "", quantity: 1, unit: "pcs", unit_price: "" }]).map((it) => {
      // Preserve blanks; if the caller passed a rate, keep it. Otherwise leave empty
      // so the "Rate" placeholder shows and users type from scratch.
      const inbound = it.unit_price ?? it.price;
      const priceStr = inbound === undefined || inbound === null || inbound === "" || Number(inbound) === 0
        ? ""
        : String(inbound);
      return {
        product: it.product || "",
        quantity: it.quantity ?? 1,
        unit: it.unit || "pcs",
        unit_price: priceStr,
        supplier_ids: [],
      };
    })
  );
  const [notes, setNotes] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiClient.get("/suppliers").then((r) => setBusinesses(r.data || [])).catch(() => {});
  }, []);

  const update = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const remove = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const addRow = () => setRows((r) => [...r, { product: "", quantity: 1, unit: "pcs", unit_price: "", supplier_ids: [] }]);

  const sameAsAbove = (i) => {
    if (i === 0) return;
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, supplier_ids: [...(r[i - 1].supplier_ids || [])] } : row)));
    toast.success("Copied businesses from above");
  };

  // Aggregate: unique destination business ids across all rows, and item counts per supplier
  const summary = useMemo(() => {
    const perSup = new Map();
    rows.forEach((r) => {
      (r.supplier_ids || []).forEach((sid) => {
        perSup.set(sid, (perSup.get(sid) || 0) + 1);
      });
    });
    return {
      distinctSuppliers: perSup.size,
      counts: perSup,
    };
  }, [rows]);

  const totals = useMemo(() => {
    const grand = rows.reduce((a, r) => a + Number(r.unit_price || 0) * Number(r.quantity || 0), 0);
    return { grand };
  }, [rows]);

  const placeOrder = async () => {
    const valid = rows.filter((r) => r.product.trim() && Number(r.quantity) > 0);
    if (valid.length === 0) { toast.error("Add at least one item"); return; }
    const anyMissingDest = valid.some((r) => !r.supplier_ids || r.supplier_ids.length === 0);
    if (anyMissingDest) { toast.error("Pick a destination business for every item"); return; }
    setSubmitting(true);
    try {
      const { data: createdOrders } = await apiClient.post("/direct-orders", {
        // supplier_ids top-level not used when items have per-row selections
        items: valid.map((r) => ({
          product: r.product.trim(),
          quantity: Number(r.quantity),
          unit: r.unit || "pcs",
          unit_price: Number(r.unit_price || 0),
          supplier_ids: r.supplier_ids,
        })),
        notes: notes.trim(),
        image_data: image_data || (initImages && initImages[0]) || null,
        images: initImages || [],
      });
      toast.success(`Placed ${createdOrders.length} order${createdOrders.length === 1 ? "" : "s"}`);
      navigate("/orders/assign", { state: { orders: createdOrders || [] }, replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  const hasBuyerImages = Array.isArray(initImages) && initImages.length > 0;

  const backButton = (
    <button onClick={() => navigate(-1)} data-testid="order-review-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
  );

  const titleBlock = (
    <div>
      <div className="inline-flex items-center gap-1.5 text-emerald-600 text-[10px] font-bold uppercase tracking-widest mb-1">
        <Sparkles className="w-3 h-3" strokeWidth={2.6} /> Review Order
      </div>
      <h2 className="text-2xl font-bold text-slate-900 leading-tight">Review & place order</h2>
      <p className="text-sm text-slate-500 mt-0.5">Route each item to its own business, then place the order</p>
    </div>
  );

  const itemsSection = (
    <Section title="Items" subtitle="Product, quantity, rate and destination business" testid="order-items-section">
      <div className="space-y-2" data-testid="order-rows">
        {rows.map((row, i) => {
          const lineTotal = Number(row.unit_price || 0) * Number(row.quantity || 0);
          return (
            <div key={i} data-testid={`order-row-${i}`} className="bg-white border border-slate-100 rounded-2xl p-2 space-y-1.5 shadow-sm">
              {/* ONE horizontal row: SN · Product · Qty · UOM · Business · Rate · Delete */}
              <div className="flex items-center gap-1.5">
                <span className="w-4 sm:w-5 shrink-0 text-center text-[11px] font-bold text-slate-400 tabular-nums">{i + 1}.</span>
                <Input
                  data-testid={`order-product-${i}`}
                  value={row.product}
                  onChange={(e) => update(i, "product", e.target.value)}
                  placeholder="Product"
                  className="h-10 rounded-lg flex-1 min-w-0 text-sm"
                />
                <Input
                  data-testid={`order-qty-${i}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={row.quantity}
                  onChange={(e) => update(i, "quantity", e.target.value)}
                  placeholder="Qty"
                  className="h-10 rounded-lg w-10 sm:w-12 text-sm text-right tabular-nums px-1.5"
                />
                <select
                  data-testid={`order-unit-${i}`}
                  value={row.unit}
                  onChange={(e) => update(i, "unit", e.target.value)}
                  className="h-10 rounded-lg border border-slate-200 px-1.5 text-sm bg-white w-[54px] sm:w-[62px] shrink-0"
                >
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <div className="min-w-[95px] flex-[1.4] shrink sm:min-w-0 sm:flex-1">
                  <BusinessMultiSelect
                    businesses={businesses}
                    selectedIds={row.supplier_ids || []}
                    onChange={(next) => update(i, "supplier_ids", next)}
                    testidPrefix={`order-biz-${i}`}
                    placeholder="Business"
                    compact
                  />
                </div>
                <Input
                  data-testid={`order-unitprice-${i}`}
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={row.unit_price}
                  onChange={(e) => update(i, "unit_price", e.target.value)}
                  placeholder="Rate"
                  className="no-spinner h-10 rounded-lg w-14 sm:w-16 text-sm text-right tabular-nums px-1.5"
                />
                <button
                  onClick={() => remove(i)}
                  data-testid={`order-delete-${i}`}
                  className="w-7 sm:w-8 h-10 shrink-0 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 grid place-items-center transition"
                  aria-label="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Compact secondary strip: Apply-to-All / Same-as-above + chips (only when relevant) */}
              {(
                (i === 0 && rows.length > 1) ||
                i > 0 ||
                (row.supplier_ids || []).length > 0
              ) && (
                <div className="flex items-center flex-wrap gap-1.5 pl-6">
                  {i === 0 && rows.length > 1 && (
                    <button
                      onClick={() => {
                        const first = rows[0]?.supplier_ids || [];
                        setRows((rs) => rs.map((r, idx) => idx === 0 ? r : { ...r, supplier_ids: [...first] }));
                        toast.success("Applied to all items");
                      }}
                      data-testid="order-apply-to-all"
                      className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 hover:text-emerald-800 normal-case shrink-0"
                    >
                      Apply to All
                    </button>
                  )}
                  {i > 0 && (
                    <button
                      onClick={() => sameAsAbove(i)}
                      data-testid={`order-same-as-above-${i}`}
                      className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 hover:text-indigo-700 flex items-center gap-1 normal-case shrink-0"
                    >
                      <CornerDownLeft className="w-3 h-3" /> Same as above
                    </button>
                  )}
                  {(row.supplier_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-1" data-testid={`order-biz-chips-${i}`}>
                      {(row.supplier_ids || []).map((sid) => {
                        const b = businesses.find((x) => x.id === sid);
                        if (!b) return null;
                        return (
                          <span
                            key={sid}
                            data-testid={`order-biz-chip-${i}-${sid}`}
                            className="inline-flex items-center gap-1 h-6 pl-2 pr-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold"
                          >
                            <span className="max-w-[100px] truncate">{b.name}</span>
                            <button
                              onClick={() => update(i, "supplier_ids", (row.supplier_ids || []).filter((x) => x !== sid))}
                              data-testid={`order-biz-chip-remove-${i}-${sid}`}
                              className="w-4 h-4 rounded-full bg-white/20 hover:bg-white/30 grid place-items-center"
                              aria-label={`Remove ${b.name}`}
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {businesses.length === 0 && (
                <div className="text-[11px] text-slate-500 bg-slate-50 rounded-lg px-2 py-1">No connected businesses yet.</div>
              )}

              <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                <span data-testid={`order-line-formula-${i}`} className="text-[11px] text-slate-500 tabular-nums">
                  ₹{Number(row.unit_price || 0).toFixed(2)} × {Number(row.quantity || 0)} {row.unit}
                </span>
                <span data-testid={`order-line-total-${i}`} className="text-sm font-bold text-slate-900 tabular-nums">= ₹{lineTotal.toFixed(2)}</span>
              </div>
            </div>
          );
        })}
        <SecondaryButton onClick={addRow} data-testid="order-add-row" className="w-full">
          <Plus className="w-4 h-4 inline mr-1" /> Add Another Item
        </SecondaryButton>
      </div>
    </Section>
  );

  const emptyBiz = businesses.length === 0 && (
    <EmptyState Icon={Building2} title="No connected businesses" subtitle="Connect a business first, then come back." />
  );

  const notesBlock = (
    <div className="space-y-1.5">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Notes (optional)</div>
      <Textarea
        data-testid="order-notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={3}
        placeholder="Delivery details, terms, or special instructions"
        className="rounded-2xl"
      />
    </div>
  );

  const totalsBlock = (
    <div data-testid="order-totals" className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex items-center justify-between">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Grand Total</div>
        <div className="text-xs text-slate-500">
          {rows.length} item{rows.length === 1 ? "" : "s"} · {summary.distinctSuppliers} business{summary.distinctSuppliers === 1 ? "" : "es"}
        </div>
      </div>
      <div className="text-2xl font-bold text-emerald-700">₹{totals.grand.toFixed(2)}</div>
    </div>
  );

  const placeButton = (
    <PrimaryButton
      onClick={placeOrder}
      loading={submitting}
      data-testid="place-order-btn"
      className="w-full h-14 flex items-center justify-center gap-2 text-base"
    >
      <ShoppingBag className="w-5 h-5" />
      Place Order {summary.distinctSuppliers > 1 && `× ${summary.distinctSuppliers}`}
    </PrimaryButton>
  );

  // Split layout: fixed-height 50/50 (top/bottom on mobile, left/right on desktop)
  // when the buyer attached images/PDF pages so they can compare the catalogue
  // page and enter rates without losing scroll position on either side.
  if (hasBuyerImages) {
    return (
      <div
        data-testid="order-review-split-layout"
        className="h-[100dvh] flex flex-col pb-[68px] lg:pb-0 bg-slate-50"
      >
        <Header />
        <div className="px-4 pt-3 pb-2 space-y-2 shrink-0 bg-white/95 backdrop-blur border-b border-slate-100">
          {backButton}
          {titleBlock}
        </div>
        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <div
            data-testid="order-review-image-half"
            className="h-1/2 md:h-full md:w-1/2 min-h-0 border-b md:border-b-0 md:border-r border-slate-200 bg-slate-900"
          >
            <QuoteImagePane images={initImages} testid="order-review-image-pane" />
          </div>
          <div
            data-testid="order-review-items-half"
            className="h-1/2 md:h-full md:w-1/2 min-h-0 overflow-y-auto bg-slate-50 p-3 space-y-3"
          >
            {itemsSection}
            {emptyBiz}
            {notesBlock}
            {totalsBlock}
            {placeButton}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-40 space-y-4">
        {backButton}
        {titleBlock}

        {image_data && (
          <div data-testid="order-image-preview" className="bg-white rounded-2xl border border-slate-100 p-2 shadow-sm">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-1 pb-2">Attached image</div>
            <img src={image_data} alt="Order" className="w-full rounded-xl object-cover max-h-64" />
          </div>
        )}

        {itemsSection}
        {emptyBiz}
        {notesBlock}
        {totalsBlock}
        {placeButton}
      </div>
    </>
  );
}
